<template>
  <BaseModal
    card-width="440px"
    no-padding
    no-toolbar
    no-exit
    @close="handleClose"
  >
    <template #default>
      <div class="relative w-full flex flex-col gap-5 p-5 border-b border-border-normal">
        <div
          class="p-2 rounded-md w-min"
          :class="['blocked', 'unknown'].includes(mode) ? 'bg-secondary-yellow-200' : 'bg-secondary-red-100'"
        >
          <DeleteIcon
            v-if="mode === 'delete'"
            class="text-white"
          />
          <WarningIcon
            v-else-if="['blocked', 'unknown'].includes(mode)"
            class="text-white"
          />
          <LeaveIcon
            v-else
            class="text-white"
          />
        </div>
        <div class="flex flex-col gap-1">
          <BaseText
            type="label"
            size="md"
            class="text-text-loud"
          >
            {{ title }}
          </BaseText>
          <BaseText
            type="body"
            size="sm"
            class="text-text-loud mt-3"
          >
            <template v-if="mode === 'leave'">
              You're already on a team named "<span class="font-medium">{{ currentTeamName }}</span>". Proceeding will leave that team, allowing you to join "<span class="font-medium">{{ displayNewTeamName }}</span>".
            </template>
            <template v-else-if="mode === 'delete'">
              You're already on a team named "<span class="font-medium">{{ currentTeamName }}</span>". Proceeding will delete that team, allowing you to join "<span class="font-medium">{{ displayNewTeamName }}</span>".
            </template>
            <template v-else-if="mode === 'unknown'">
              You're already on a team named "<span class="font-medium">{{ currentTeamName }}</span>", but we couldn't verify your role on it. Manage your team before you can join "<span class="font-medium">{{ displayNewTeamName }}</span>".
            </template>
            <template v-else>
              You're already on a team named "<span class="font-medium">{{ currentTeamName }}</span>", and it still has {{ otherMemberLabel }}. Remove them before you can join "<span class="font-medium">{{ displayNewTeamName }}</span>".
            </template>
          </BaseText>
        </div>
        <div
          v-if="loading"
          class="flex items-center justify-center gap-2 overflow-hidden status-enter-anim"
          style="height: 20px"
        >
          <BaseLoadingSpinnerCircle
            class="text-text-subdued"
            :duration="1"
          />
          <BaseText
            type="body"
            size="sm"
            class="text-text-muted text-center"
          >
            {{ mode === 'delete' ? 'Deleting' : 'Leaving' }} team. This can take a minute...
          </BaseText>
        </div>
        <button
          class="absolute top-3 right-3 p-1 transition-opacity"
          :class="loading ? 'opacity-50 cursor-default' : 'opacity-100'"
          :disabled="loading"
          @click="handleClose"
        >
          <TagRemoveIcon
            stroke="#5E6678"
            :width="16"
            :height="16"
          />
        </button>
      </div>
      <div class="w-full flex flex-col gap-2 p-5">
        <button
          class="submit-btn w-full py-2 px-3 rounded-md flex items-center justify-center"
          :class="[primaryVariant, loading ? 'disabled cursor-default' : 'enabled']"
          :disabled="loading"
          @click="handlePrimary"
        >
          <BaseLoadingSpinner
            v-if="loading"
            small
          />
          <BaseText
            v-else
            type="label"
            size="sm"
            class="text-center"
          >
            {{ primaryLabel }}
          </BaseText>
        </button>
        <button
          class="w-full px-3 py-2 rounded-md transition duration-100 hover:bg-neutral-25"
          :class="{ 'opacity-50 cursor-default': loading }"
          :disabled="loading"
          @click="handleSecondary"
        >
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted text-center"
          >
            {{ secondaryLabel }}
          </BaseText>
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import firebase from '@/api/config/FirebaseConfig'
import ForeplayAPI from '@/api/foreplayServer'
import FirebaseAPI from '@/api/firebase'
import CloudFunctionsAPI from '@/api'

import DeleteIcon from '../globals/Icons/DeleteIcon.vue'
import LeaveIcon from '../globals/Icons/LeaveIcon.vue'
import WarningIcon from '../globals/Icons/WarningIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'

export default {
  name: 'TeamInviteResolveTeamModal',
  components: {
    DeleteIcon,
    LeaveIcon,
    WarningIcon,
    TagRemoveIcon
  },
  props: {
    // The team the user is currently on, from the /respond USER_ALREADY_ON_TEAM error
    team: {
      type: Object,
      required: true
    },
    // The team on the invite they are trying to accept
    newTeamName: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      loading: false
    }
  },
  computed: {
    mode () {
      const isOwner = this.team?.isOwner
      if (typeof isOwner !== 'boolean') return 'unknown'
      if (!isOwner) return 'leave'
      const memberCount = Number(this.team.memberCount)
      if (!Number.isInteger(memberCount) || memberCount < 1) return 'unknown'
      return memberCount > 1 ? 'blocked' : 'delete'
    },
    currentTeamName () {
      return this.team.teamName || 'your current team'
    },
    displayNewTeamName () {
      return this.newTeamName || 'the new team'
    },
    otherMemberCount () {
      const memberCount = Number(this.team.memberCount) || 0
      return Math.max(memberCount - 1, 0)
    },
    otherMemberLabel () {
      return this.otherMemberCount === 1 ? '1 member' : `${this.otherMemberCount} members`
    },
    title () {
      if (this.mode === 'leave') return 'Leave your current team?'
      if (this.mode === 'delete') return 'Delete current team to join?'
      if (this.mode === 'unknown') return 'Couldn\'t verify your team'
      return 'You can\'t join yet'
    },
    primaryLabel () {
      if (this.mode === 'leave') return `Leave & Join "${this.displayNewTeamName}"`
      if (this.mode === 'delete') return `Delete Team & Join "${this.displayNewTeamName}"`
      return 'Manage Team'
    },
    secondaryLabel () {
      if (this.mode === 'leave') return 'Stay on this team'
      if (this.mode === 'delete') return 'Nevermind'
      if (this.mode === 'unknown') return 'Close'
      return 'Decline Invite'
    },
    primaryVariant () {
      return this.mode === 'delete' ? 'danger' : 'primary'
    }
  },
  methods: {
    handlePrimary () {
      if (['blocked', 'unknown'].includes(this.mode)) {
        this.$emit('manage-team')
        return
      }
      this.handleProceed()
    },
    handleSecondary () {
      if (this.loading) return
      if (this.mode === 'blocked') {
        this.$emit('decline')
        return
      }
      this.handleClose()
    },
    async handleProceed () {
      this.loading = true
      try {
        const uid = firebase.auth().currentUser.uid

        // Wait for the migration job too - accepting while it runs would migrate the user into the new team concurrently
        if (this.mode === 'delete') {
          await CloudFunctionsAPI.Teams.deleteTeam(this.team.id)
          await FirebaseAPI.Teams.waitForDeleteTeamJob(this.team.id)
        } else {
          await ForeplayAPI.Teams.manageTeamMember(uid, this.team.id, 'leave')
          await CloudFunctionsAPI.Teams.migrateUserFromTeam(uid, this.team.id)
          await FirebaseAPI.Teams.waitForTeamRemovalJob(uid, this.team.id)
        }

        this.$emit('resolved')
      } catch (error) {
        console.error(error)
        // cloud functions send { error: { message } }, the express server sends { message }
        const errorMessage = error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          `Error occurred while ${this.mode === 'delete' ? 'deleting' : 'leaving'} your team.`
        this.$showAlert({
          type: 'error',
          message: errorMessage
        })
        this.$emit('close')
      } finally {
        this.loading = false
      }
    },
    handleClose () {
      if (this.loading) return
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-color {
  syntax: '<color>';
  initial-value: #0063F4;
  inherits: false;
}
@property --submit-ring {
  syntax: '<color>';
  initial-value: #0063F4;
  inherits: false;
}
.submit-btn {
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-ring);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out,
    --submit-ring 100ms ease-in-out;
}
.submit-btn.primary {
  --submit-color: #0063F4;
  --submit-ring: #0063F4;
}
.submit-btn.danger {
  --submit-color: #F4003A;
  --submit-ring: #C80031;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
  --submit-ring: #F6F8FA;
}

.status-enter-anim {
  animation: statusEnter 200ms ease-in-out forwards;
}
@keyframes statusEnter {
  0% { height: 0px; opacity: 0; }
  50% { height: 20px; opacity: 0; }
  100% { height: 20px; opacity: 1; }
}
</style>
